<template>
    <SelectAutocomplete :options="options" placeholder="Select option" rounded="full" size="md" v-model="selectedTimeZone" variant="light" />
</template>

<script setup lang="ts">
    import { ref, computed, watch } from 'vue'
    import SelectAutocomplete from './SelectAutocomplete.vue'
    import type { SelectAutocompleteProps } from './SelectAutocomplete.vue'
    import { rawTimeZones } from '@vvo/tzdb'
    import { DateTime } from 'luxon'

    const LOCALES = {
        en: 'en',
        es: 'es',
        nl: 'nl',
        de: 'de',
        fr: 'fr',
        pt: 'pt'
    } as const
    type Locale = (typeof LOCALES)[keyof typeof LOCALES]

    const props = withDefaults(
        defineProps<{
            modelValue?: string
            defaultTimeZone?: 'local' | 'utc'
            locale?: Locale
        }>(),
        {
            defaultTimeZone: 'local',
            locale: 'en'
        }
    )

    const emit = defineEmits(['update:modelValue', 'timeZoneChange'])

    // Add translations ref and loading state
    const translations = ref<Record<string, string>>({})
    const isLoadingTranslations = ref(false)

    // Add watch effect to load translations when locale changes
    watch(
        () => props.locale,
        async (newLocale) => {
            isLoadingTranslations.value = true
            try {
                const localeTranslations = await import(`../../locales/timeZones/${newLocale}.json`).then((res) => res.default)
                translations.value = localeTranslations
            } catch (error) {
                console.warn(`Failed to load translations for locale: ${newLocale}`, error)
                // Fallback to English or empty object if load fails
                translations.value = {}
            } finally {
                isLoadingTranslations.value = false
            }
        },
        { immediate: true }
    )

    type TimeZone = {
        alternativeName: string
        countryName: string
        formatted: string
        group: string[]
        mainCities: string[]
        name: string
        rawOffsetInMinutes: number
    }

    /**
     * UTC timezone object
     */
    const UtcTimeZone = {
        alternativeName: 'Universal Time Coordinated',
        countryName: '',
        formatted: '+00:00 Universal Time Coordinated',
        group: [],
        mainCities: [],
        name: 'UTC',
        rawOffsetInMinutes: 0
    } as const

    /**
     * All timezones
     */
    const timeZones = [UtcTimeZone, ...rawTimeZones]
    const timeZoneWithLocales = computed(() => {
        return timeZones.map((timeZone) => {
            const translation = translations.value[timeZone.name]
            return { ...timeZone, label: translation?.replaceAll('_', ' ') }
        })
    })

    const localTimeZone = computed(() => timeZones.find((timeZone) => timeZone.name === DateTime.local().zoneName))

    /**
     * Define model value
     */
    const selectedTimeZone = ref(props.modelValue || '')

    watch(selectedTimeZone, (newValue) => {
        if (!newValue) {
            selectedTimeZone.value = 'utc'
            emit('update:modelValue', 'utc')
            return
        }
        if (newValue === localTimeZone.value?.name) {
            emit('update:modelValue', 'system')
        } else if (newValue === UtcTimeZone.name) {
            emit('update:modelValue', 'utc')
        } else {
            emit('update:modelValue', newValue)
        }
        // Find and emit the full timezone object
        const selectedTimeZoneObject = timeZones.find((timeZone) => timeZone.name === newValue)
        emit('timeZoneChange', selectedTimeZoneObject)
    })

    // Set default value when not defined
    const defaultTimeZone = computed(() => (props.defaultTimeZone === 'utc' ? UtcTimeZone : localTimeZone.value))
    if (!selectedTimeZone.value) {
        selectedTimeZone.value = defaultTimeZone.value?.name ?? ''
    } else if (selectedTimeZone.value === 'system') {
        selectedTimeZone.value = localTimeZone.value?.name ?? ''
    } else if (selectedTimeZone.value === 'utc') {
        selectedTimeZone.value = UtcTimeZone.name
    }

    /**
     * Options for the select component
     */
    const options = computed(() =>
        timeZoneWithLocales.value.map((timeZone) => {
            if (timeZone.name === UtcTimeZone.name) {
                return {
                    label: timeZone.name,
                    value: timeZone.name,
                    subLabel: 'Universal Time Coordinated',
                    displayValue: '(GMT+00:00) UTC'
                }
            } else {
                const label = timeZone.label ?? timeZone.name.replaceAll('_', ' ')
                const offsetInMinutes = timeZone.rawOffsetInMinutes
                const formattedOffsetInHours = formatMinutesToHHMM(offsetInMinutes)
                const subLabel = `GMT${offsetInMinutes >= 0 ? '+' : '-'}${formattedOffsetInHours}`
                const displayValue = `(${subLabel}) ${label}`
                return { label, value: timeZone.name, subLabel, displayValue }
            }
        })
    )

    /**
     * Helper functions
     */
    const formatMinutesToHHMM = (totalMinutes: number) => {
        const hours = Math.floor(Math.abs(totalMinutes) / 60)
        const minutes = Math.abs(totalMinutes) % 60
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    }
</script>
